import { useAuth } from "$/util/auth";
import Constants from "@blackink/core/categoryConstants";
import CheckIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  ButtonBase,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
  styled,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React from "react";
import { Link } from "react-router-dom";
import ProBadge from "./icons/ProBadge";

const ImageButton = styled(ButtonBase)(({ theme }) => ({
  position: "relative",
  height: 120,
  width: 120,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    width: "100% !important", // Overrides inline-style
    height: 150,
  },
  "&:hover, &.Mui-focusVisible, &.Mui-selected": {
    zIndex: 1,
    borderColor: theme.palette.primary.main,
    cursor: "pointer",
    "& .MuiImageBackdrop-root": {
      opacity: 0,
    },
    "& .MuiTypography-root": {
      color: theme.palette.primary.main,
    },
  },
}));

const ImageSrc = styled("span")(({ theme }) => ({
  position: "absolute",
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  backgroundSize: "cover",
  backgroundPosition: "center 40%",
}));

const ImageContent = styled("span")(({ theme }) => ({
  position: "absolute",
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  display: "flex",
  alignItems: "end",
  justifyContent: "center",
  color: theme.palette.info.contrastText,
}));

const ImageBackdrop = styled("span")(({ theme }) => ({
  position: "absolute",
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  // Add a gradient to the image for better readability
  backgroundImage: "linear-gradient(to top, rgba(255,255,255,1), rgba(255,255,255,0))",
  transition: theme.transitions.create("opacity"),
}));

function StyleSelectModal(props) {
  const auth = useAuth();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const midScreen = useMediaQuery(theme.breakpoints.only("md"));

  const isPro = (auth.user && auth.user.isActiveSubscription) || props.showAll;

  const handleStyleSelect = (style) => {
    props.onClose(style);
  };

  // eslint-disable-next-line no-nested-ternary
  const leftPos = fullScreen ? 0 : midScreen ? 110 : 5;

  return (
    <Dialog
      open={props.open}
      onClose={() => props.onClose(null)}
      fullScreen={fullScreen}
      hideBackdrop
      maxWidth={false}
      // scroll="body"
      disableAutoFocus
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: theme.palette.background.default,
          borderRadius: 2,
          border: fullScreen ? "none" : `1px solid ${theme.palette.info.main}`,
          boxShadow: "2px 2px 6px rgba(0, 0, 0, 0.1)",
          maxHeight: fullScreen ? "100%" : "60vh",
          maxWidth: fullScreen ? "100%" : 425,
          left: leftPos,
          // Hide scrollbar
          "&::-webkit-scrollbar": {
            display: "none",
          },
        },
      }}
    >
      {fullScreen && (
        <>
          <DialogTitle>Select A Style</DialogTitle>
          <IconButton
            aria-label="close"
            onClick={() => props.onClose(null)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              zIndex: 100,
              backgroundColor: "white",
              "&:hover": {
                backgroundColor: "#e0e0e0",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </>
      )}
      <DialogContent
        sx={{
          p: 2,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Typography component="div" variant="subtitle3">
            Common Styles
          </Typography>
        </Stack>
        <Grid container spacing={2}>
          {Constants.FREE_STYLES.map((category) => (
            <Grid item xs={6} sm={4} md={4} key={category}>
              <ImageButton
                onClick={() => handleStyleSelect(category)}
                sx={{
                  border:
                    props.style === category
                      ? `4px solid ${theme.palette.primary.main}`
                      : `1px solid ${theme.palette.divider}`,
                }}
              >
                <ImageSrc
                  style={{
                    backgroundImage: `url(${`/images/styles/${category}.webp`})`,
                  }}
                />
                <ImageBackdrop className="MuiImageBackdrop-root" />
                <ImageContent>
                  {props.style === category && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                      }}
                    >
                      <CheckIcon color="primary" />
                    </Box>
                  )}
                  <Typography
                    component="span"
                    variant="subtitle2"
                    color="inherit"
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {Constants.CATEGORY_DESC[category].name}
                  </Typography>
                </ImageContent>
              </ImageButton>
            </Grid>
          ))}
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-between"
          sx={{ mt: 2, mb: 1 }}
        >
          <Typography component="div" variant="subtitle3">
            Special Styles
          </Typography>
          {!isPro && (
            <Chip
              component={Link}
              clickable
              label={
                <>
                  <ProBadge
                    color={theme.palette.primary.contrastText}
                    height={18}
                    sx={{ ml: -0.5, mb: -0.4 }}
                  />
                  Pro Only
                </>
              }
              color="primary"
              size="small"
              sx={{ mx: 1 }}
              to="/pricing"
            />
          )}
        </Stack>

        <Grid container spacing={2}>
          {Constants.PAID_STYLES.map((category) => (
            <Grid item xs={6} sm={4} md={4} key={category}>
              <ImageButton
                onClick={() => handleStyleSelect(category)}
                sx={{
                  border:
                    props.style === category
                      ? `4px solid ${theme.palette.primary.main}`
                      : `1px solid ${theme.palette.divider}`,
                  opacity: !isPro ? 0.5 : 1,
                }}
                disabled={!isPro}
              >
                <ImageSrc
                  style={{
                    backgroundImage: `url(${`/images/styles/${category}.webp`})`,
                  }}
                />
                <ImageBackdrop className="MuiImageBackdrop-root" />
                <ImageContent>
                  {props.style === category && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                      }}
                    >
                      <CheckIcon color="primary" />
                    </Box>
                  )}
                  <Typography
                    component="span"
                    variant="subtitle2"
                    color="inherit"
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {Constants.CATEGORY_DESC[category].name}
                  </Typography>
                </ImageContent>
              </ImageButton>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

export default StyleSelectModal;
